import { api } from '@/api';
import {
  type ErrorHandlerFunction,
  InstallationConfigDto,
  UpdateConnectionStringsRequestDto,
} from '@/api/generator';
import { useVeeForm } from '@/components/forms/useVeeForm';
import {
  catchAllErrorHandler,
  formBackendErrorHandler,
  stopErrorPropagation,
} from '@/errorHandling';
import { AnyApiError } from '@/errorHandling/typeGuards';
import {
  buildConnectionString,
  type ConnectionStringParams,
  createDatabaseValidation,
  createInitialValues,
  renderMaskedConnectionString,
} from '@/utils/database';
import { useDatabaseJobState } from '@/utils/useDatabaseJobState';
import { useLoading } from '@/utils/useLoading';
import { useTimeoutFn } from '@vueuse/core';
import { useFieldValue } from 'vee-validate';
import { computed, onBeforeMount, ref, watch } from 'vue';
import { useAuthenticationStore } from '@/stores/authenticationStore.ts';
import { storeToRefs } from 'pinia';

/**
 * Options for the database settings composable
 */
interface UseDatabaseSettingsOptions {
  /**
   * Whether to automatically load installation config on mount
   * Set to false for the installation wizard where config comes from props
   */
  autoLoad?: boolean;
  /**
   * Initial installation config (for installation wizard)
   */
  initialConfig?: InstallationConfigDto | null;
  errorHandlers?: ErrorHandlerFunction<AnyApiError>[];
  /**
   * Callback function called after reboot completes successfully
   */
  afterReboot?: () => void | Promise<void>;
}

/**
 * Unified database settings composable that handles:
 * - Form state and validation
 * - Database type management
 * - API calls and job state
 * - Both installation wizard and settings scenarios
 */
export function useDatabaseSettings(options: UseDatabaseSettingsOptions = {}) {
  const { autoLoad = true, initialConfig = null, errorHandlers, afterReboot } = options;

  // Core state
  const currentInstallationConfig = ref(initialConfig);
  const isCustomDatabaseUsed = ref<boolean>();
  const databaseType = ref<'integrated' | 'custom'>('integrated');

  // UI state
  const isDefaultPasswordVisible = ref(false);
  const isBlobStoringPasswordVisible = ref(false);
  const showSuccessMessage = ref(false);
  const showErrorMessage = ref(false);
  const jobSuccess = ref(false);
  const pendingSuccessCallback = ref<(() => void) | undefined>();
  const isSubmitting = ref(false);
  const wasRebooting = ref(false);
  const isForBlobPwUnchangedSet = ref(false);
  const isForDefaultPwUnchangedSet = ref(false);

  // Smart loader state to prevent flashing
  const isLoaderVisible = ref(false);
  const runningJobPingCount = ref(0);
  const REQUIRED_PINGS_TO_SHOW_LOADER = 2; // Require 2 consecutive pings with "Running" state
  const MINIMUM_LOADER_DISPLAY_TIME = 1000; // Keep the loader visible for at least 1 second once shown

  const authenticationStore = useAuthenticationStore();
  const { session } = storeToRefs(authenticationStore);

  // Create a form with shared validation
  const { form: databaseSettingsForm } = useVeeForm<UpdateConnectionStringsRequestDto>({
    schemaName: 'UpdateConnectionStringsRequestDto',
    mode: 'edit',
    customValidateFunctions: [createDatabaseValidation()],
  });

  const { handleSubmit, setFieldValue, resetForm, meta, validate, values } = databaseSettingsForm;

  // Form field watchers for live connection string updates
  const defaultServer = useFieldValue<string | undefined>('defaultConnectionString.server');
  const defaultPort = useFieldValue<number | undefined>('defaultConnectionString.port');
  const defaultDatabase = useFieldValue<string | undefined>('defaultConnectionString.database');
  const defaultUser = useFieldValue<string | undefined>('defaultConnectionString.user');
  const defaultPassword = useFieldValue<string | undefined>('defaultConnectionString.password');
  const defaultAdditionalParams = useFieldValue<string | undefined>(
    'defaultConnectionString.additionalParameters',
  );

  const blobServer = useFieldValue<string | undefined>('blobStoringConnectionString.server');
  const blobPort = useFieldValue<number | undefined>('blobStoringConnectionString.port');
  const blobDatabase = useFieldValue<string | undefined>('blobStoringConnectionString.database');
  const blobUser = useFieldValue<string | undefined>('blobStoringConnectionString.user');
  const blobPassword = useFieldValue<string | undefined>('blobStoringConnectionString.password');
  const blobAdditionalParams = useFieldValue<string | undefined>(
    'blobStoringConnectionString.additionalParameters',
  );

  // Job state management
  function handleSuccess(lastJobHasCompleted: boolean) {
    if (lastJobHasCompleted && jobSuccess.value) {
      showSuccessMessage.value = true;
      pendingSuccessCallback.value?.();
      pendingSuccessCallback.value = undefined;

      // Call afterReboot callback if we were rebooting and now finished
      if (wasRebooting.value) {
        afterReboot?.();
      }
      wasRebooting.value = false;
    }
  }

  function handleFailure() {
    showErrorMessage.value = true;
    pendingSuccessCallback.value = undefined;
  }

  const { isActive, resumePollingForDatabaseJobState, isRebooting, jobState } = useDatabaseJobState(
    {
      onSuccess: handleSuccess,
      onFailure: handleFailure,
    },
  );

  // Smart loader logic to prevent flashing
  const { start: startMinimumDisplayTimer, stop: stopMinimumDisplayTimer } = useTimeoutFn(() => {
    isLoaderVisible.value = false;
    runningJobPingCount.value = 0;
  }, MINIMUM_LOADER_DISPLAY_TIME);

  // Watch job state to control smart loader visibility
  watch([jobState, isActive], ([newJobState, newIsActive]) => {
    if (!newIsActive || !newJobState) {
      // Polling stopped or no job state-reset counters
      runningJobPingCount.value = 0;
      if (!isLoaderVisible.value) {
        // If the loader wasn't shown yet, don't show it
        return;
      }
      // If the loader is already visible, start the minimum display timer
      startMinimumDisplayTimer();
      return;
    }

    if (newJobState.state === 'Running') {
      runningJobPingCount.value++;

      // Show loader only after the required number of consecutive "Running" pings
      if (runningJobPingCount.value >= REQUIRED_PINGS_TO_SHOW_LOADER && !isLoaderVisible.value) {
        isLoaderVisible.value = true;
        stopMinimumDisplayTimer(); // Cancel any pending hide timer
      }
    } // Job is not running anymore
    else if (isLoaderVisible.value) {
      // Loader is visible, start the minimum display timer before hiding
      startMinimumDisplayTimer();
    } else {
      // Loader wasn't visible, just reset
      runningJobPingCount.value = 0;
    }
  });

  // Computed for reboot state - using jobState directly
  const isWaitingForReboot = computed(
    () =>
      jobState.value?.state === 'Completed' &&
      jobState.value?.rebootRequired &&
      !jobState.value?.rebootIsDone &&
      !isRebooting.value,
  );

  // Watch for reboot status to track when the reboot starts
  watch(isRebooting, (newValue) => {
    if (newValue) {
      wasRebooting.value = true;
    }
  });

  // Computed connection strings for display with password masking and live updates
  const defaultConnectionString = computed(() => {
    if (!currentInstallationConfig.value) return '';

    if (databaseType.value === 'custom') {
      // Use live form values when in custom mode
      const params: ConnectionStringParams = {
        server: defaultServer.value,
        port: defaultPort.value,
        database: defaultDatabase.value,
        user: defaultUser.value,
        password: defaultPassword.value,
        additionalParameters: defaultAdditionalParams.value,
      };
      return buildConnectionString(params, isDefaultPasswordVisible.value);
    }

    // Integrated mode: render the original string with masked/unmasked password, no param parsing
    const connectionString = currentInstallationConfig.value.integratedDefaultConnectionString;
    return renderMaskedConnectionString(connectionString, isDefaultPasswordVisible.value);
  });

  const blobStoringConnectionString = computed(() => {
    if (!currentInstallationConfig.value) return '';

    if (databaseType.value === 'custom') {
      // Use live form values when in custom mode
      const params: ConnectionStringParams = {
        server: blobServer.value,
        port: blobPort.value,
        database: blobDatabase.value,
        user: blobUser.value,
        password: blobPassword.value,
        additionalParameters: blobAdditionalParams.value,
      };
      return buildConnectionString(params, isBlobStoringPasswordVisible.value);
    }

    // Integrated mode: render the original string with masked/unmasked password, no param parsing
    const connectionString = currentInstallationConfig.value.integratedBlobConnectionString;
    return renderMaskedConnectionString(connectionString, isBlobStoringPasswordVisible.value);
  });

  // Form initialization
  function resetToInitialValues(
    dbType: 'integrated' | 'custom',
    withoutConnectionStrings?: boolean,
  ) {
    if (!currentInstallationConfig.value) return;

    const initialValues = createInitialValues(
      currentInstallationConfig.value,
      dbType,
      withoutConnectionStrings,
      session.value?.isFirstTimeInstallation,
    );

    if (
      databaseType.value === 'custom' &&
      initialValues.blobStoringConnectionString?.password === '<unchanged>'
    ) {
      initialValues.blobStoringConnectionString.password = '';
      isForBlobPwUnchangedSet.value = true;
    }
    if (
      databaseType.value === 'custom' &&
      initialValues.defaultConnectionString?.password === '<unchanged>'
    ) {
      initialValues.defaultConnectionString.password = '';
      isForDefaultPwUnchangedSet.value = true;
    }

    resetForm({ values: initialValues }, { force: true });
  }

  function handleDatabaseTypeChange(dbType: 'integrated' | 'custom') {
    if (dbType !== 'integrated' && dbType !== 'custom') return;

    databaseType.value = dbType;

    // If switching to custom and not currently using a custom database, clear form
    const withoutConnectionStrings =
      dbType === 'custom' && !currentInstallationConfig.value?.isCustomDatabaseUsed;
    resetToInitialValues(dbType, withoutConnectionStrings);
  }

  // Update installation config (for installation wizard)
  function updateInstallationConfig(config: InstallationConfigDto | null) {
    currentInstallationConfig.value = config;
    if (config) {
      const dbType = config.isCustomDatabaseUsed ? 'custom' : 'integrated';
      databaseType.value = dbType;
      isCustomDatabaseUsed.value = config.isCustomDatabaseUsed;
      resetToInitialValues(dbType);
    }
  }

  // Load installation config from API (for settings view)
  const { action: loadInstallationConfig, loading } = useLoading(async () => {
    const { success: isSuccess, result } = await api.installApi.getInstallationConfig();
    if (isSuccess && result) {
      updateInstallationConfig(result);
    }
  }, true);

  // Watch for external config changes
  watch(
    () => options.initialConfig,
    (newConfig) => {
      if (newConfig) {
        updateInstallationConfig(newConfig);
      }
    },
    { immediate: true },
  );

  // Password visibility management
  function toggleDefaultPasswordVisibility() {
    isDefaultPasswordVisible.value = !isDefaultPasswordVisible.value;
  }

  function toggleBlobStoringPasswordVisibility() {
    isBlobStoringPasswordVisible.value = !isBlobStoringPasswordVisible.value;
  }

  // Message management
  function hideMessages() {
    showSuccessMessage.value = false;
    showErrorMessage.value = false;
    jobSuccess.value = false;
  }

  // Submit database settings
  async function submitDatabaseSettings(
    updateConnectionStringsRequestDto: UpdateConnectionStringsRequestDto,
    onSuccessCallback?: () => void,
  ) {
    hideMessages();
    isSubmitting.value = true;

    try {
      const { success: jobSuccessfullyStarted } = await api.installApi.updateConnectionStrings(
        {
          updateConnectionStringsRequestDto,
        },
        [
          formBackendErrorHandler(databaseSettingsForm, true),
          ...(errorHandlers || []),
          catchAllErrorHandler,
          // Keep this order: earlier handlers may stop propagation to avoid duplicate error
          // messages (e.g. maintenance mode). `catchAllErrorHandler` returns `error` rather
          // than suppressing it, so this trailing entry is what actually stops the chain.
          stopErrorPropagation,
        ],
      );

      jobSuccess.value = jobSuccessfullyStarted;

      if (jobSuccessfullyStarted) {
        pendingSuccessCallback.value = onSuccessCallback;
        resumePollingForDatabaseJobState();
      }

      return jobSuccessfullyStarted;
    } finally {
      isSubmitting.value = false;
    }
  }

  // Auto-load on mount if enabled
  onBeforeMount(async () => {
    if (autoLoad) {
      await loadInstallationConfig();
    }
  });

  return {
    // Form
    form: databaseSettingsForm,
    handleSubmit,
    setFieldValue,
    resetForm,
    meta,
    validate,
    values,

    // Core state
    currentInstallationConfig,
    isCustomDatabaseUsed,
    databaseType,

    // UI state
    loading,
    isActive: isLoaderVisible, // Use smart loader visibility
    isSubmitting,
    isDefaultPasswordVisible,
    isBlobStoringPasswordVisible,
    isRebooting,
    isWaitingForReboot,
    showSuccessMessage,
    showErrorMessage,
    isForBlobPwUnchangedSet,
    isForDefaultPwUnchangedSet,

    // Computed values
    defaultConnectionString,
    blobStoringConnectionString,

    // Methods
    handleDatabaseTypeChange,
    updateInstallationConfig,
    loadInstallationConfig,
    submitDatabaseSettings,
    toggleDefaultPasswordVisibility,
    toggleBlobStoringPasswordVisibility,
    hideMessages,
  };
}
